/*
 * Copyright (c) 2021.
 * This file is part of the Compo B2B application.
 * (c) Compo soft - Nikita Tropin <ntropin@yandex.ru>
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import {Cart} from '../interfaces/Cart';
import {CartProduct} from '../interfaces/CartProduct';
import {Environment} from '../interfaces/Environment';
import {ProductCustomer} from '../interfaces/ProductCustomer';
import {Observable, of, Subscription} from 'rxjs';
import {CartEventService} from './cart-event.service';
import {Inject, Injectable, OnDestroy} from '@angular/core';
import {CompoApiService} from './compo-api.service';
import {catchError, map, tap} from 'rxjs/operators';
import {CartBaseService} from './cart-base.service';
import {CartProductAmount} from '../interfaces/CartProductAmount';
import {StoreService} from './store.service';
import {CustomProduct} from '../interfaces/CustomProduct';
import {AmountProduct} from '../interfaces/AmountProduct';
import {CartProductReplace} from '../interfaces/CartProductReplace';
import {CustomerGroup} from '../interfaces/CustomerGroup';

@Injectable({
  providedIn: 'root'
})

export class CartRemoteService extends CartBaseService implements Cart, OnDestroy {
  customerGroupId = 0;
  readonly customerGroup$ = this.storeService.getCustomerGroup();
  private readonly customerGroupSubscription: Subscription;

  constructor(private readonly apiService: CompoApiService,
              private readonly storeService: StoreService,
              protected readonly cartEventService: CartEventService,
              @Inject('environment') protected readonly environment: Environment) {
    super(cartEventService, environment);
                console.log('Cart remote service')
    this.customerGroupSubscription = this.customerGroup$.pipe(tap(data => this.handleCustomerGroup(data))).subscribe();
  }

  ngOnDestroy(): void {
    if (this.customerGroupSubscription) {
      this.customerGroupSubscription.unsubscribe();
    }
  }

  private handleCustomerGroup(customerGroup: CustomerGroup) {
    if (customerGroup) {
      this.customerGroupId = customerGroup.id;
    }
  }

  recount(cartProduct: CartProduct, amount: number): Observable<CartProduct> {
    return this._recount(cartProduct, amount).pipe(
      tap(resp => this.updateCartProduct(resp)),
      tap(resp => this.doNextCartProduct(resp)),
      tap(_ => this.doNext())
    );
  }

  protected _recount(cartProduct: CartProduct, amount: number): Observable<CartProduct> {
    const id = cartProduct.id;
    return this.apiService.post<CartProductAmount, CartProduct>(
      '/cart/recount?region=' + this.customerGroupId, {id, amount}).pipe(map(resp => resp.data)
    );
  }

  add(product: ProductCustomer, amount: number): Observable<CartProduct> {
    if (amount <= 0) { return of<CartProduct>(); }

    return this.push(product, amount).pipe(
      tap(resp => this.updateCartProduct(resp)),
      tap(resp => this.doNextCartProduct(resp)),
      tap(_ => this.doNext())
    );
  }

  addList(cartProductList: CartProduct[]): Observable<CartProduct[]> {
      return this.pushList(cartProductList).pipe(
        tap(resp => this.cart = resp),
        tap(_ => this.storeService.delCart()),
        tap(_ => this.doNext())
      );
  }

  clear(): Observable<any> {
    return this.pushClear().pipe(
      tap(_ => this.cart.length = 0),
      tap(_ => this.doNext())
      );
  }

  get(): Observable<CartProduct[]> {
    const storeCart = this.getStoreCart();
    if (storeCart.length) {
      return this.addList(storeCart);
    }

    return this.getCart().pipe(
      tap((resp: CartProduct[]) => this.cart = resp),
      tap(_ => this.doNext())
    );
  }

  getShareCart(syncUid): Observable<CartProduct[]> {
    return this.getShareCartByApi(syncUid).pipe(
      tap((resp: CartProduct[]) => this.cart = resp),
      tap(_ => this.doNext())
    );
  }

  getByRegion(region: number): Observable<CartProduct[]> {
    const storeCart = this.getStoreCart();
    if (storeCart.length) {
      return this.addList(storeCart);
    }

    return this.getCartByRegion(region).pipe(
      tap((resp: CartProduct[]) => this.cart = resp),
      tap(_ => this.doNext())
    );
  }

  getStoreCart(): CartProduct[] {
    return this.storeService.getCart();
  }

  remove(cartProduct: CartProduct): Observable<any> {
      return this.pushRemove(cartProduct.id).pipe(tap(_ => this.removeCartProduct(cartProduct)));
  }

  download(): Observable<Blob> {
    return this.apiService.downloadUrl('/cart/download').pipe(catchError(err => this.handleError(err)));
  }
 // TODO :
  private getCart(): Observable<CartProduct[]> {
    console.log("get cart")
    return this.apiService.get<CartProduct[]>('/cart').pipe(map(resp => resp.data));
  }

  private getShareCartByApi(syncUid): Observable<CartProduct[]> {
    return this.apiService.get<CartProduct[]>('/cart/share/'+syncUid).pipe(map(resp => resp.data));
  }

  private getCartByRegion(region: number): Observable<CartProduct[]> {
    return this.apiService.get<CartProduct[]>('/cart?region=' + region).pipe(map(resp => resp.data));
  }

  protected push(product: ProductCustomer, amount: number): Observable<CartProduct> {
    const productId = product.id;
    return this.apiService.post<AmountProduct, CartProduct>(
      '/cart/add-product?region=' + this.customerGroupId, {productId, amount}).pipe(map(resp => resp.data)
    );
  }

  private pushList(cartProductList: CartProduct[]): Observable<CartProduct[]> {
    return this.apiService.post<CartProduct[], CartProduct[]>(
      '/cart/add-list?region=' + this.customerGroupId, cartProductList).pipe(map(resp => resp.data)
    );
  }

  private pushRemove(id: number): Observable<any> {
    return this.apiService.remove('/cart/' + id).pipe(catchError(err => this.handleError(err)));
  }

  private pushClear(): Observable<any> {
    return this.apiService.remove('/cart/clear').pipe(
      catchError(err => this.handleError(err))
    );
  }

  private updateCartProduct(cartProduct: CartProduct) {
    const index = this.findIndex(cartProduct.id);
    if (index === -1) {
      this.cart.push(cartProduct);
    } else {
      this.cart[index] = cartProduct;
    }
  }

  private removeCartProduct(cartProduct: CartProduct) {
    const index = this.findIndex(cartProduct.id);
    if (index > -1) {
      this.cart.splice(index, 1);
      this.doNext();
    }
  }

  addCustom(product: CustomProduct): Observable<CartProduct> {
    return this.pushCustom(product).pipe(
      tap(resp => this.updateCartProduct(resp)),
      tap(resp => this.doNextCartProduct(resp)),
      tap(_ => this.doNext())
    );
  }

  private pushCustom(product: CustomProduct): Observable<CartProduct> {
    return this.apiService.postForm<CustomProduct, CartProduct>('/cart/add-custom', product).pipe(
      map(response => response.data)
    );
  }

  replace(cartProductReplace: CartProductReplace): Observable<any> {
    return this.apiService.post<CartProductReplace, any>('/cart/replace', cartProductReplace);
  }
}
